import React, { useState } from "react";
import { Button, PopupModal, ErrorMessage } from "../../shared";
import DropFileInput from "../../shared/DropFileInput/DropFileInput";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import axiosInstance from "axiosConfig";
import "./UpdatePricesModal.scss";

interface ProductChange {
  ean: string;
  currentPrice?: number;
  newPrice: number;
  excelPrice?: number;
  marginApplied?: number;
  isNew: boolean;
  priceChanged: boolean;
}

interface UploadResponse {
  message: string;
  changes: ProductChange[];
  summary: {
    total: number;
    new: number;
    priceChanges: number;
    noChanges: number;
  };
}

interface ProgressUpdate {
  progress: number;
  status: string;
  message: string;
}

interface UpdatePricesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const UpdatePricesModal: React.FC<UpdatePricesModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [step, setStep] = useState<"upload" | "preview">("upload");
  const [uploadResponse, setUploadResponse] = useState<UploadResponse | null>(
    null
  );
  const [uploadProgress, setUploadProgress] = useState<ProgressUpdate | null>(
    null
  );
  const [confirmProgress, setConfirmProgress] = useState<ProgressUpdate | null>(
    null
  );
  const [eventSourceRef, setEventSourceRef] = useState<EventSource | null>(
    null
  );

  const validation = () =>
    Yup.object().shape({
      file: Yup.mixed()
        .required("Plik jest wymagany")
        .test(
          "fileType",
          "Dozwolone są tylko pliki Excel (.xlsx, .xls)",
          (value) => {
            if (!value) return false;
            const file = value as File;
            const allowedTypes = [
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
              "application/vnd.ms-excel",
            ];
            return (
              allowedTypes.includes(file.type) ||
              file.name.endsWith(".xlsx") ||
              file.name.endsWith(".xls")
            );
          }
        )
        .test("fileSize", "Plik nie może być większy niż 50MB", (value) => {
          if (!value) return false;
          const file = value as File;
          return file.size <= 50 * 1024 * 1024; // 10MB
        }),
    });

  const initialValues = {
    file: null as File | null,
  };

  const onSubmit = async (values: typeof initialValues) => {
    let eventSource: EventSource | null = null;

    try {
      setIsUploading(true);
      setShowError(false);
      setErrorMessage("");
      setUploadProgress({
        progress: 0,
        status: "Rozpoczynanie",
        message: "Trwa przesyłanie pliku...",
      });

      // Create FormData for file upload
      const formData = new FormData();
      if (values.file) {
        formData.append("file", values.file);
      }

      // Upload file and get taskId immediately
      const response = await axiosInstance.post("/prices/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      const taskId = response.data.taskId;

      if (!taskId) {
        throw new Error("No task ID received");
      }

      // Set up Server-Sent Events to track progress
      const baseURL = axiosInstance.defaults.baseURL || "";
      const streamUrl = `${baseURL}/prices/progress/${taskId}/stream`;

      eventSource = new EventSource(streamUrl, { withCredentials: true });
      setEventSourceRef(eventSource);

      // Wait for processing to complete
      await new Promise<void>((resolve, reject) => {
        eventSource!.onmessage = (event) => {
          const progressData: ProgressUpdate = JSON.parse(event.data);
          setUploadProgress(progressData);

          // Close connection when complete
          if (progressData.progress >= 100) {
            if (
              progressData.status === "completed" ||
              progressData.status === "Ukończono"
            ) {
              resolve();
            } else if (progressData.status === "error") {
              reject(new Error(progressData.message));
            }
          }
        };

        eventSource!.onerror = (error) => {
          reject(new Error("Connection error while processing file"));
        };
      });

      // Fetch the final result
      const resultResponse = await axiosInstance.get(
        `/prices/result/${taskId}`
      );

      eventSource?.close();
      setEventSourceRef(null);

      setUploadResponse(resultResponse.data);
      setStep("preview");
    } catch (error: any) {
      setShowError(true);
      setErrorMessage(
        error.response?.data?.message ||
          error.message ||
          "Błąd podczas przesyłania pliku"
      );
      eventSource?.close();
      setEventSourceRef(null);
    } finally {
      setIsUploading(false);
    }
  };

  const handleConfirmChanges = async () => {
    if (!uploadResponse) return;

    let eventSource: EventSource | null = null;

    try {
      setIsConfirming(true);
      setShowError(false);
      setErrorMessage("");
      setConfirmProgress({
        progress: 0,
        status: "Rozpoczynanie",
        message: "Rozpoczynanie aktualizacji...",
      });

      const response = await axiosInstance.post("/prices/confirm", {
        changes: uploadResponse.changes,
      });

      const taskId = response.data.taskId;

      if (!taskId) {
        throw new Error("No task ID received");
      }

      // Set up Server-Sent Events to track progress
      const baseURL = axiosInstance.defaults.baseURL || "";
      const streamUrl = `${baseURL}/prices/progress/${taskId}/stream`;

      eventSource = new EventSource(streamUrl, { withCredentials: true });
      setEventSourceRef(eventSource);

      // Wait for processing to complete
      await new Promise<void>((resolve, reject) => {
        eventSource!.onmessage = (event) => {
          const progressData: ProgressUpdate = JSON.parse(event.data);
          setConfirmProgress(progressData);

          // Close connection when complete
          if (progressData.progress >= 100) {
            if (
              progressData.status === "completed" ||
              progressData.status === "Ukończono"
            ) {
              resolve();
            } else if (progressData.status === "error") {
              reject(new Error(progressData.message));
            }
          }
        };

        eventSource!.onerror = (error) => {
          reject(new Error("Błąd połączenia podczas aktualizacji cen"));
        };
      });

      // Fetch the final result
      const resultResponse = await axiosInstance.get(
        `/prices/confirm-result/${taskId}`
      );

      eventSource?.close();
      setEventSourceRef(null);

      console.log("Confirmation completed:", resultResponse.data);

      // Small delay to ensure backend updates are complete
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Call onSuccess callback to refetch data
      onSuccess?.();

      // Small delay to ensure UI updates
      await new Promise((resolve) => setTimeout(resolve, 300));

      handleClose();
    } catch (error: any) {
      setShowError(true);
      setErrorMessage(
        error.response?.data?.message ||
          error.message ||
          "Błąd podczas aktualizacji cen"
      );
      eventSource?.close();
      setEventSourceRef(null);
    } finally {
      setIsConfirming(false);
      setConfirmProgress(null);
    }
  };

  const handleBackToUpload = () => {
    setStep("upload");
    setUploadResponse(null);
    setUploadProgress(null);
    setConfirmProgress(null);
    setShowError(false);
    setErrorMessage("");
  };

  const handleClose = () => {
    // Clean up event source if still open
    if (eventSourceRef) {
      eventSourceRef.close();
      setEventSourceRef(null);
    }

    setShowError(false);
    setErrorMessage("");
    setStep("upload");
    setUploadResponse(null);
    setUploadProgress(null);
    setConfirmProgress(null);
    onClose();
  };

  const renderUploadStep = () => (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={validation()}
    >
      {({ errors, values, handleSubmit, setFieldValue }) => (
        <Form>
          <ErrorMessage
            message={errorMessage}
            isVisible={showError}
            onClose={() => setShowError(false)}
          />

          <DropFileInput
            label="Plik Excel"
            name="file"
            accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
          />

          {isUploading && uploadProgress && (
            <div className="upload-progress">
              <div className="upload-progress__bar-container">
                <div
                  className="upload-progress__bar"
                  style={{ width: `${uploadProgress.progress}%` }}
                />
              </div>
              <div className="upload-progress__info">
                <span className="upload-progress__percentage">
                  {Math.round(uploadProgress.progress)}%
                </span>
                <span className="upload-progress__message">
                  {uploadProgress.message}
                </span>
              </div>
            </div>
          )}

          <div className="popup-modal__buttons">
            <Button
              label="Anuluj"
              styleType="stroke"
              onClick={handleClose}
              type="button"
              disabled={isUploading}
            />
            <Button
              label={
                isUploading
                  ? uploadProgress
                    ? `${Math.round(uploadProgress.progress)}% - ${
                        uploadProgress.status
                      }`
                    : "Przetwarzanie..."
                  : "Sprawdź zmiany"
              }
              styleType="primary"
              onClick={() => handleSubmit()}
              type="submit"
              disabled={isUploading || !values.file}
            />
          </div>
        </Form>
      )}
    </Formik>
  );

  const renderPreviewStep = () => {
    if (!uploadResponse) return null;

    const { changes, summary } = uploadResponse;

    return (
      <div className="update-prices-modal__preview">
        <ErrorMessage
          message={errorMessage}
          isVisible={showError}
          onClose={() => setShowError(false)}
        />

        <div className="update-prices-modal__summary">
          <h3>Podsumowanie zmian</h3>
          <div className="summary-stats">
            <div className="stat">
              <span className="stat__label">Sprawdzonych produktów:</span>
              <span className="stat__value">{summary.total}</span>
            </div>
            <div className="stat">
              <span className="stat__label">Zmiany cen:</span>
              <span className="stat__value stat__value--changed">
                {summary.priceChanges}
              </span>
            </div>
            <div className="stat">
              <span className="stat__label">Bez zmian:</span>
              <span className="stat__value">{summary.noChanges}</span>
            </div>
          </div>
        </div>

        <div className="update-prices-modal__changes">
          <h4>Szczegóły zmian cen</h4>
          <div className="changes-list">
            {changes
              .filter((c) => c.priceChanged)
              .map((change, index) => (
                <div key={index} className="change-item change-item--changed">
                  <div className="change-item__ean">{change.ean}</div>
                  <div className="change-item__prices">
                    <div className="price-row">
                      <span className="price-label">Cena przed marżą:</span>
                      <span className="price price--excel">
                        {change.excelPrice?.toFixed(2) ||
                          change.newPrice.toFixed(2)}{" "}
                        zł
                      </span>
                    </div>
                    {change.marginApplied && change.marginApplied > 1 && (
                      <div className="price-row">
                        <span className="price-label">Marża:</span>
                        <span className="price price--margin">
                          x{change.marginApplied}
                        </span>
                      </div>
                    )}
                    <div className="price-row">
                      <span className="price-label">VAT:</span>
                      <span className="price price--margin">x 1.23</span>
                    </div>
                    <div className="price-row">
                      <span className="price-label">Nowa cena:</span>
                      <span className="price price--new">
                        {change.newPrice.toFixed(2)} zł
                      </span>
                    </div>
                    <div className="price-row price-row--comparison">
                      <span className="price-label">Obecna cena:</span>
                      <span className="price price--old">
                        {change.currentPrice?.toFixed(2)} zł
                      </span>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>

        {isConfirming && confirmProgress && (
          <div className="upload-progress">
            <div className="upload-progress__bar-container">
              <div
                className="upload-progress__bar"
                style={{ width: `${confirmProgress.progress}%` }}
              />
            </div>
            <div className="upload-progress__info">
              <span className="upload-progress__percentage">
                {Math.round(confirmProgress.progress)}%
              </span>
              <span className="upload-progress__message">
                {confirmProgress.message}
              </span>
            </div>
          </div>
        )}

        <div className="popup-modal__buttons">
          <Button
            label="Wróć"
            styleType="stroke"
            onClick={handleBackToUpload}
            type="button"
            disabled={isConfirming}
          />
          <Button
            label={
              isConfirming
                ? confirmProgress
                  ? `${Math.round(confirmProgress.progress)}% - ${
                      confirmProgress.status
                    }`
                  : "Aktualizowanie..."
                : "Potwierdź zmiany"
            }
            styleType="primary"
            onClick={handleConfirmChanges}
            type="button"
            disabled={isConfirming || summary.priceChanges === 0}
          />
        </div>
      </div>
    );
  };

  return (
    <PopupModal isOpen={isOpen} onClose={handleClose}>
      <div className="update-prices-modal">
        <div className="update-prices-modal__header">
          <h2 className="popup-modal__header">
            {step === "upload" ? "Aktualizuj ceny" : "Podgląd zmian"}
          </h2>
        </div>

        {step === "upload" ? renderUploadStep() : renderPreviewStep()}
      </div>
    </PopupModal>
  );
};

export default UpdatePricesModal;
